import type {
	Checkout,
	CheckoutPayment,
	CheckoutStep,
	CheckoutSummary,
	Country,
	DeclarationAnswer,
	DeclarationQuestion,
	DeliveryOptionPayload,
	DeliveryOptions,
	DriverDetailsPayload,
	IdentificationPayload,
	PaymentPayload,
	PickupLocation,
	SecondaryDriverDetails,
	ServicePlan,
} from '@/types/order-checkout'
import {
	getCheckoutErrorMessage,
	getCheckoutErrors,
	type CheckoutErrors,
} from '@/utils/order-checkout'
import { acceptHMRUpdate, defineStore } from 'pinia'

interface OrderCheckoutState {
	// The API's checkout state, the source of truth for every step
	checkout: Checkout | null
	summary: CheckoutSummary | null
	servicePlans: Record<string, ServicePlan>
	pickupLocations: PickupLocation[]
	pickupState: string | null
	countries: Country[]
	declarationQuestions: DeclarationQuestion[]
	payment: CheckoutPayment | null
	secondaryDriver: SecondaryDriverDetails | null
	isReopening: boolean

	errors: CheckoutErrors | null
}

type CheckoutResponse = { data: { item: Checkout } }

// HubSpot's visitor cookie, for the checkout progress the API reports to HubSpot
const hubspotHeaders = (): Record<string, string> => {
	const utk = useCookie<string | null>('hubspotutk').value
	return utk ? { 'X-HubSpot-UTK': utk } : {}
}

export const useOrderCheckout = defineStore('orderCheckout', {
	state: (): OrderCheckoutState => ({
		checkout: null,
		summary: null,
		servicePlans: {},
		pickupLocations: [],
		pickupState: null,
		countries: [],
		declarationQuestions: [],
		payment: null,
		secondaryDriver: null,
		isReopening: false,

		errors: null,
	}),

	getters: {
		hasSecondaryDriverOption: (state): boolean =>
			Boolean(state.secondaryDriver?.firstName),
		promotions: (state) => state.checkout?.promotions ?? [],
		selectedServicePlan: (state): ServicePlan | null => {
			const offer = state.checkout?.plan?.service_plan_offer
			return offer ? (state.servicePlans[offer] ?? null) : null
		},
		// A confirmed order that can't go back: a payment has been attempted, or Loopit can't be reached
		isOrderLocked: (state): boolean =>
			state.checkout?.status === 'pending_payment' &&
			!state.checkout.can_return_to_summary,

		hasErrors: (state) => state.errors,
	},

	actions: {
		async loadCheckout(uuid: string) {
			if (this.checkout?.uuid !== uuid) {
				this.$reset()
			}

			const { data } = (await useFetchForApi(
				`/v2/checkout/${uuid}`
			)) as CheckoutResponse

			this.syncCheckout(data.item)
		},

		// Refetches the state after a failed request, keeping the current one if this fails too
		async refreshCheckout() {
			try {
				await this.loadCheckout(this.checkout!.uuid)
			} catch {
				// The failed request's error is already shown
			}
		},

		// Steps respond with the full checkout state. Prices depend on the plan and promo codes, so they're refetched.
		syncCheckout(checkout: Checkout) {
			this.checkout = checkout

			if (checkout.plan) {
				this.fetchSummary()
			}
		},

		request<T = CheckoutResponse>(
			path: string,
			options: Record<string, unknown> = {}
		): Promise<T> {
			return useFetchForApi(
				`/v2/checkout/${this.checkout?.uuid}${path}`,
				options
			) as Promise<T>
		},

		// Runs a step's request behind reCAPTCHA, then moves on to the next step.
		async submitStep(
			recaptchaAction: string,
			send: () => Promise<CheckoutResponse>,
			nextStep: CheckoutStep
		) {
			this.errors = null

			const token = await useExecuteRecaptcha(recaptchaAction)
			const recaptcha = await useVerifyRecaptcha(token)

			if (!recaptcha?.isVerified) {
				// A string message is for the customer; Google's error codes come as an array
				this.errors = {
					recaptcha: [
						typeof recaptcha?.message === 'string' &&
						recaptcha.message
							? recaptcha.message
							: 'We could not verify your request. Please refresh the page and try again.',
					],
				}
				return
			}

			try {
				const { data } = await send()
				this.syncCheckout(data.item)
			} catch (error) {
				this.errors = getCheckoutErrors(error)
				return
			}

			return navigateTo(`/checkout/${this.checkout?.uuid}/${nextStep}`)
		},

		// Step - Driver
		submitDriverDetails(
			driver: DriverDetailsPayload,
			secondaryDriver: SecondaryDriverDetails | null
		) {
			this.secondaryDriver = secondaryDriver

			return this.submitStep(
				'order_driver_details_step',
				() =>
					this.request('/driver', {
						method: 'PUT',
						body: driver,
						headers: hubspotHeaders(),
					}),
				'plan'
			)
		},

		clearSecondaryDriverOption() {
			this.secondaryDriver = null
		},

		// Step - Plan
		async fetchServicePlans() {
			const { data } = (await useFetchForApi(
				`/v2/vehicles/${this.checkout?.vehicle.uuid}`
			)) as {
				data: { item: { service_plans?: Record<string, ServicePlan> } }
			}

			this.servicePlans = data.item.service_plans ?? {}
		},

		submitPlan(term: number | null) {
			return this.submitStep(
				'order_plan_details_step',
				() =>
					this.request('/plan', {
						method: 'PUT',
						body: { term },
						headers: hubspotHeaders(),
					}),
				'delivery'
			)
		},

		// Step - Delivery Option
		async fetchPickupLocations() {
			const { data } = await this.request<{ data: DeliveryOptions }>(
				'/delivery-options'
			)

			this.pickupLocations = data.locations
			this.pickupState = data.pickup_state
		},

		submitDeliveryOption(deliveryOption: DeliveryOptionPayload) {
			return this.submitStep(
				'order_delivery_option_step',
				() =>
					this.request('/delivery', {
						method: 'PUT',
						body: deliveryOption,
						headers: hubspotHeaders(),
					}),
				'eligibility'
			)
		},

		// Step - Eligibility
		async fetchCountries() {
			try {
				const { data } = await this.request<{
					data: { countries: Country[] }
				}>('/countries')
				this.countries = data.countries
			} catch (error) {
				this.errors = getCheckoutErrors(error)
			}
		},

		async uploadBankStatement(file: File) {
			const body = new FormData()
			body.append('bank_statement', file)

			try {
				const { data } = await this.request('/bank-statements', {
					method: 'POST',
					body,
				})
				this.syncCheckout(data.item)
			} catch (error) {
				throw new Error(getCheckoutErrorMessage(error))
			}
		},

		async removeBankStatement(id: number) {
			const { data } = await this.request(`/bank-statements/${id}`, {
				method: 'DELETE',
			})
			this.syncCheckout(data.item)
		},

		submitIdentification(identification: IdentificationPayload) {
			return this.submitStep(
				'order_eligibility_step',
				() =>
					this.request('/identification', {
						method: 'PUT',
						body: identification,
						headers: hubspotHeaders(),
					}),
				'declaration'
			)
		},

		// Step - Declaration
		async fetchDeclarationQuestions() {
			try {
				const { data } = await this.request<{
					data: { item: { questions: DeclarationQuestion[] } }
				}>('/declaration')
				this.declarationQuestions = data.item.questions
			} catch (error) {
				this.errors = getCheckoutErrors(error)
			}
		},

		submitDeclaration(declarations: Record<string, DeclarationAnswer>) {
			return this.submitStep(
				'order_declaration_step',
				() =>
					this.request('/declaration', {
						method: 'PUT',
						body: { declarations, terms: true },
					}),
				'summary'
			)
		},

		async cancelOrder() {
			try {
				await useFetchForApi(
					`/v2/presales/${this.checkout?.uuid}/cancel`,
					{ method: 'PATCH' }
				)
			} catch (error) {
				throw new Error(getCheckoutErrorMessage(error))
			}

			this.$reset()

			return navigateTo('/dashboard')
		},

		// Step - Summary
		async fetchSummary() {
			try {
				const { data } = await this.request<{
					data: { item: CheckoutSummary }
				}>('/summary')

				this.summary = data.item
			} catch {
				this.summary = null
			}
		},

		async applyPromoCode(promocode: string) {
			try {
				const { data } = await this.request('/promo-codes', {
					method: 'POST',
					body: { code: promocode.trim().toUpperCase() },
				})
				this.syncCheckout(data.item)
			} catch (error) {
				throw new Error(getCheckoutErrorMessage(error))
			}
		},

		async removePromoCode(promocode: string) {
			const { data } = await this.request(
				`/promo-codes/${encodeURIComponent(promocode)}`,
				{ method: 'DELETE' }
			)
			this.syncCheckout(data.item)
		},

		// A PDF, so it's fetched without useFetchForApi's JSON handling
		async fetchAgreement(): Promise<Blob> {
			try {
				return (await $fetchForApi(
					`/v2/checkout/${this.checkout?.uuid}/agreement`,
					{ responseType: 'blob' }
				)) as Blob
			} catch (error) {
				// The JSON error body arrives as a Blob too
				const body = (error as { data?: unknown })?.data
				const data =
					body instanceof Blob
						? await body
								.text()
								.then(JSON.parse)
								.catch(() => undefined)
						: body

				throw new Error(getCheckoutErrorMessage({ data }))
			}
		},

		// Raises the order; the presale then only accepts payment.
		confirmOrder() {
			return this.submitStep(
				'order_summary_step',
				() =>
					this.request('/confirm', {
						method: 'POST',
						body: { terms: true },
					}),
				'payment'
			)
		},

		// Step - Payment
		async fetchPayment() {
			this.errors = null

			try {
				const { data } = await this.request<{ data: CheckoutPayment }>(
					'/payment'
				)
				this.payment = data
			} catch (error) {
				this.errors = getCheckoutErrors(error)
			}
		},

		async submitPayment(payment: PaymentPayload) {
			await this.submitStep(
				'order_payment_step',
				() =>
					this.request('/payment', {
						method: 'POST',
						body: payment,
						headers: hubspotHeaders(),
					}),
				'thank-you'
			)

			// A declined card leaves a booking in Loopit, which stops the order going back
			if (this.errors) {
				await this.refreshCheckout()
			}
		},

		// Takes a confirmed order back to an earlier step. The API refuses once a payment has been attempted.
		async returnToStep(step: CheckoutStep) {
			this.errors = null
			this.isReopening = true

			try {
				const { data } = await this.request('/reopen', {
					method: 'POST',
				})
				this.syncCheckout(data.item)
			} catch (error) {
				this.errors = getCheckoutErrors(error)
				await this.refreshCheckout()
				return
			} finally {
				this.isReopening = false
			}

			return navigateTo(`/checkout/${this.checkout?.uuid}/${step}`)
		},

		// Step - Thank you
		async completeOrder() {
			try {
				const { data } = await this.request('/complete', {
					method: 'POST',
				})
				this.syncCheckout(data.item)
			} catch (error) {
				this.errors = getCheckoutErrors(error)
			}
		},
	},
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useOrderCheckout, import.meta.hot))
}
